import { movies } from "@/mocks/movies";
import type { MediaItem, MediaType } from "@/types/movie";

export const allMedia = movies as unknown as MediaItem[];

export type SortKey = "hot" | "new" | "rating";

export interface FilterOptions {
  type?: MediaType | "all";
  genres?: string[];
  year?: string;
  region?: string;
  keyword?: string;
  sort?: SortKey;
}

export function getMediaById(id?: string): MediaItem | undefined {
  if (!id) return undefined;
  return allMedia.find((item) => item.id === id);
}

function sortMedia(list: MediaItem[], sort: SortKey = "hot"): MediaItem[] {
  const copy = [...list];
  if (sort === "new") {
    copy.sort((a, b) => (a.addedAt < b.addedAt ? 1 : -1));
  } else if (sort === "rating") {
    copy.sort((a, b) => b.rating - a.rating);
  } else {
    copy.sort((a, b) => b.popularity - a.popularity);
  }
  return copy;
}

export function filterMedia(options: FilterOptions = {}): MediaItem[] {
  const { type = "all", genres = [], year = "all", region = "all", keyword = "", sort = "hot" } = options;
  const kw = keyword.trim().toLowerCase();

  const result = allMedia.filter((item) => {
    if (type !== "all" && item.type !== type) return false;
    if (genres.length > 0 && !genres.some((g) => item.genres.includes(g))) return false;
    if (year !== "all" && String(item.year) !== String(year)) return false;
    if (region !== "all" && item.region !== region) return false;
    if (kw) {
      const haystack = [item.title, item.director, item.region, ...item.genres, ...item.actors]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(kw)) return false;
    }
    return true;
  });

  return sortMedia(result, sort);
}

export function getGenreOptions(type: MediaType | "all" = "all"): string[] {
  const set = new Set<string>();
  allMedia.forEach((item) => {
    if (type === "all" || item.type === type) {
      item.genres.forEach((g) => set.add(g));
    }
  });
  return Array.from(set);
}

export function getYearOptions(): string[] {
  const set = new Set<number>();
  allMedia.forEach((item) => set.add(item.year));
  return Array.from(set)
    .sort((a, b) => b - a)
    .map((y) => String(y));
}

export function getRegionOptions(): string[] {
  const set = new Set<string>();
  allMedia.forEach((item) => set.add(item.region));
  return Array.from(set);
}

export function getHotMedia(limit: number): MediaItem[] {
  return sortMedia(allMedia, "hot").slice(0, limit);
}

export function getLatestMedia(limit: number): MediaItem[] {
  return sortMedia(allMedia, "new").slice(0, limit);
}

export function getRelatedMedia(item: MediaItem, limit = 5): MediaItem[] {
  const scored = allMedia
    .filter((m) => m.id !== item.id)
    .map((m) => {
      let score = 0;
      if (m.type === item.type) score += 3;
      score += m.genres.filter((g) => item.genres.includes(g)).length * 2;
      if (m.region === item.region) score += 1;
      return { m, score };
    })
    .sort((a, b) => b.score - a.score || b.m.popularity - a.m.popularity);
  return scored.slice(0, limit).map((s) => s.m);
}